import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'; // useRef removed: unused import
// import { Layer } from 'react-konva'; // Commented: unused import

const LayerContext = createContext();

export const useLayers = () => useContext(LayerContext);

// Helper function to make objects safe for logging
// const makeSafeForLogging = (obj) => {
//   if (!obj) return obj;
//   
//   // Create a new object without circular references
//   const safeObj = { ...obj };
//   
//   // Remove Konva refs that cause circular references
//   if (safeObj.konvaLayerRef) {
//     delete safeObj.konvaLayerRef;
//   }
//   
//   return safeObj;
// }; // Commented: unused function

// Generate unique layer ID using timestamp and random string - moved outside component for stability
const generateLayerId = () => {
  return `layer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const LayerProvider = ({ children, mapId }) => {
  const [layers, setLayers] = useState([]);
  const [activeLayerId, setActiveLayerId] = useState(null);
  const [activeLayer, setActiveLayer] = useState(null);
  const [layerGroups, setLayerGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Keep activeLayer in sync with layers
  useEffect(() => {
    if (activeLayerId) {
      const layer = layers.find(l => l.layer_id === activeLayerId);
      setActiveLayer(layer || null);
    }
  }, [layers, activeLayerId]);

  // Create a new layer - client-side only, no server call
  const createLayer = useCallback((layerData) => {
    try {
      // Construct the new layer first so we can return it synchronously
      const newLayer = {
        layer_id: layerData.layer_id || generateLayerId(),
        layer_name: layerData.layer_name || 'New Layer',
        layer_order: layerData.layer_order !== undefined ? layerData.layer_order : (layers?.length || 0),
        is_visible: layerData.is_visible !== undefined ? layerData.is_visible : true,
        is_locked: layerData.is_locked !== undefined ? layerData.is_locked : false,
        opacity: layerData.opacity !== undefined ? layerData.opacity : 1,
        is_wall_layer: layerData.is_wall_layer || false,
        is_fow_layer: layerData.is_fow_layer || false,
        is_background_layer: layerData.is_background_layer || false,
        is_grid_layer: layerData.is_grid_layer || false,
        is_token_layer: layerData.is_token_layer || false,
        is_drawing_layer: layerData.is_drawing_layer || false,
        is_image_layer: layerData.is_image_layer || false,
        layer_type: layerData.layer_type || 'general',
        created_at: layerData.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
        drawings: layerData.drawings || [],
        images: layerData.images || [],
        layer_data: layerData.layer_data || JSON.stringify({ lines: [] })
      };

      setLayers(prevLayers => {
        const finalLayers = [...prevLayers, newLayer].sort((a, b) => a.layer_order - b.layer_order);
        if (finalLayers.length === 1) {
          setActiveLayer(newLayer);
          setActiveLayerId(newLayer.layer_id);
        }
        return finalLayers;
      });

      return newLayer; // Return the created layer object with its ID
    } catch (error) {
      console.error('[LayerContext] Error creating layer:', error);
      throw error;
    }
  }, [layers]);

  // Update an existing layer - client-side only
  const updateLayer = async (layerId, layerData) => {
    setLoading(true);
    setError(null);
    
    try {
      let updatedLayer = null;
      
      setLayers(prevLayers => {
        const updatedLayers = prevLayers.map(layer => {
          if (layer.layer_id === layerId) {
            updatedLayer = {
              ...layer,
              ...layerData
            };
            return updatedLayer;
          }
          return layer;
        });
        return updatedLayers;
      });
      
      // Removed verbose logging here

      return updatedLayer;
    } catch (err) {
      console.error('Error updating layer:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Delete a layer - client-side only
  const deleteLayer = useCallback((layerId) => {
    try {
      const layerToDelete = layers.find(l => l.layer_id === layerId);
      if (!layerToDelete) {
        console.warn('[LayerContext] Layer not found for deletion:', layerId);
        return false;
      }

      setLayers(prevLayers => {
        const remainingLayers = prevLayers.filter(layer => layer.layer_id !== layerId);
        return remainingLayers;
      });

      // If the deleted layer was active, set a new active layer
      if (activeLayer?.layer_id === layerId) {
        const remainingLayers = layers.filter(layer => layer.layer_id !== layerId);
        if (remainingLayers.length > 0) {
          setActiveLayerByObject(remainingLayers[0]);
        } else {
          setActiveLayerByObject(null);
        }
      }

      return true;
    } catch (error) {
      console.error('[LayerContext] Error deleting layer:', error);
      return false;
    }
  }, [layers, activeLayer]);

  // Delete multiple layers at once - client-side only
  const deleteLayers = useCallback((layerIds) => {
    try {
      setLayers(prevLayers => {
        const remainingLayers = prevLayers.filter(layer => !layerIds.includes(layer.layer_id));
        return remainingLayers;
      });

      // If the active layer was deleted, set a new active layer
      if (activeLayer && layerIds.includes(activeLayer.layer_id)) {
        const remainingLayers = layers.filter(layer => !layerIds.includes(layer.layer_id));
        if (remainingLayers.length > 0) {
          setActiveLayerByObject(remainingLayers[0]);
        } else {
          setActiveLayerByObject(null);
        }
      }

      return true;
    } catch (error) {
      console.error('[LayerContext] Error deleting layers:', error);
      return false;
    }
  }, [layers, activeLayer]);

  // Reorder layers - client-side only
  const reorderLayers = async (newOrder) => {
    setLoading(true);
    setError(null);
    
    try {
  
      
      // Create a copy of the current layers
      let orderedLayers = [...layers];
      
      // Sort the layers based on the new order of IDs
      orderedLayers.sort((a, b) => {
        return newOrder.indexOf(a.layer_id) - newOrder.indexOf(b.layer_id);
      });
      
      // Update the layer_order property for each layer
      orderedLayers = orderedLayers.map((layer, index) => ({
        ...layer,
        layer_order: index
      }));
      
      // Update state with the new ordered layers
      setLayers(orderedLayers);
      

      return true;
    } catch (err) {
      console.error('Error reordering layers:', err);
      setError('Failed to reorder layers: ' + err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Set active layer
  const setActiveLayerById = (layerId) => {
    setActiveLayerId(layerId);
    const layer = layers.find(l => l.layer_id === layerId);
    setActiveLayer(layer || null);
  };

  // Set active layer by layer object
  const setActiveLayerByObject = (layer) => {
    setActiveLayer(layer);
    setActiveLayerId(layer ? layer.layer_id : null);
  };

  // Get active layer
  const getActiveLayer = () => {
    return activeLayer || layers.find(layer => layer.layer_id === activeLayerId);
  };

  // Restore a layer (for undo operations)
  const restoreLayer = async (layerData) => {
    setLoading(true);
    setError(null);
    
    try {
      // Create a new layer with the same data and try to preserve the original ID
      const restoredLayer = {
        layer_id: layerData.layer_id, // Try to keep the original ID
        layer_name: layerData.layer_name,
        is_wall_layer: layerData.is_wall_layer || false,
        layer_order: layerData.layer_order !== undefined ? layerData.layer_order : layers.length,
        is_visible: layerData.is_visible !== false,
        layer_data: layerData.layer_data || JSON.stringify({ lines: [] }),
        konvaLayerRef: React.createRef()
      };
      
      setLayers(prevLayers => {
        // Check if a layer with this ID already exists
        const existingLayer = prevLayers.find(l => l.layer_id === restoredLayer.layer_id);
        if (existingLayer) {
          // Update existing layer instead of adding duplicate
          return prevLayers.map(layer => 
            layer.layer_id === restoredLayer.layer_id ? restoredLayer : layer
          );
        } else {
          // Add new layer and sort by layer_order
          const updatedLayers = [...prevLayers, restoredLayer];
          return updatedLayers.sort((a, b) => a.layer_order - b.layer_order);
        }
      });
      
  
      return restoredLayer;
    } catch (err) {
      console.error('Error restoring layer:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Generate unique group ID
  const generateGroupId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 6);
    return `group-${timestamp}-${random}`;
  };

  // Create default 2 layers for existing games when logging in
  const createDefaultGameLayers = useCallback(() => {
    try {
      
      // Clear layers and layer groups first to avoid conflicts
      setLayers([]);
      setLayerGroups([]);
      
      // Create 2 default layers with fixed order values to ensure proper stacking
      // Lower layer_order = bottom, Higher layer_order = top
      
      // Layer 1: Maps Layer - Bottom layer (order: 0)
      const mapsLayer = {
        layer_id: generateLayerId(),
        layer_name: "Maps Layer",
        layer_order: 0, // Bottom
        is_visible: true,
        is_locked: false,
        opacity: 1,
        is_wall_layer: false,
        is_fow_layer: false,
        is_background_layer: false,
        is_grid_layer: false,
        is_token_layer: false,
        is_drawing_layer: false,
        is_image_layer: true,
        layer_type: 'image',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        drawings: [],
        images: [],
        layer_data: JSON.stringify({ 
          containers: [],
          layerType: 'IMAGE_LAYER'
        })
      };

      // Layer 2: Fog of War Layer - Top layer (order: 1)
      const fowLayer = {
        layer_id: generateLayerId(),
        layer_name: "Fog of War Layer",
        layer_order: 1, // Top
        is_visible: true,
        is_locked: false,
        opacity: 1,
        is_wall_layer: true,
        is_fow_layer: true,
        is_background_layer: false,
        is_grid_layer: false,
        is_token_layer: false,
        is_drawing_layer: false,
        is_image_layer: false,
        layer_type: 'fow',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        drawings: [],
        images: [],
        layer_data: JSON.stringify({ 
          containers: [],
          layerType: 'FOW_LAYER'
        })
      };

      // Set all layers at once to avoid race conditions
      // Sort by layer_order to ensure correct display order
      const allLayers = [mapsLayer, fowLayer].sort((a, b) => a.layer_order - b.layer_order);
      
      setLayers(allLayers);
      
      // Set the Maps layer as active by default
      setActiveLayer(mapsLayer);
      setActiveLayerId(mapsLayer.layer_id);
      
      // Create layer group with a small delay to ensure layers are set in state
      setTimeout(() => {
        // Default Group: Maps Layer (bottom) + FOW Layer (top)
        const groupId = generateGroupId();
        const group = {
          id: groupId,
          name: "Default Group",
          layerIds: [mapsLayer.layer_id, fowLayer.layer_id],
          color: "#2c3e50"
        };
        
        setLayerGroups([group]);
      }, 100);
      
      // Successfully created default 2 layers for existing game
      
    } catch (error) {
      console.error('[LayerContext] Error creating default game layers:', error);
    }
  }, []);

  // Create initial 4 layers for new games in the correct order
  const createInitialGameLayers = useCallback(() => {
    try {
      
      // Clear layers and layer groups first to avoid conflicts
      setLayers([]);
      setLayerGroups([]);
      
      // Create layers with fixed order values to ensure proper stacking
      // The order should be: FOW(Group2) -> Maps(Group2) -> FOW(Group1) -> Maps(Group1)
      // Lower layer_order = bottom, Higher layer_order = top
      
      // Layer 1: Maps Layer (Group 1) - Bottom layer (order: 0)
      const mapsLayer1 = {
        layer_id: generateLayerId(),
        layer_name: "Maps Layer",
        layer_order: 0, // Bottom
        is_visible: true,
        is_locked: false,
        opacity: 1,
        is_wall_layer: false,
        is_fow_layer: false,
        is_background_layer: false,
        is_grid_layer: false,
        is_token_layer: false,
        is_drawing_layer: false,
        is_image_layer: true,
        layer_type: 'image',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        drawings: [],
        images: [],
        layer_data: JSON.stringify({ 
          containers: [],
          layerType: 'IMAGE_LAYER'
        })
      };

      // Layer 2: Fog of War Layer (Group 1) - Second from bottom (order: 1)
      const fowLayer1 = {
        layer_id: generateLayerId(),
        layer_name: "Fog of War Layer",
        layer_order: 1,
        is_visible: true,
        is_locked: false,
        opacity: 1,
        is_wall_layer: true,
        is_fow_layer: true,
        is_background_layer: false,
        is_grid_layer: false,
        is_token_layer: false,
        is_drawing_layer: false,
        is_image_layer: false,
        layer_type: 'fow',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        drawings: [],
        images: [],
        layer_data: JSON.stringify({ 
          containers: [],
          layerType: 'FOW_LAYER'
        })
      };

      // Layer 3: Maps Layer (Group 2) - Second from top (order: 2)
      const mapsLayer2 = {
        layer_id: generateLayerId(),
        layer_name: "Maps Layer",
        layer_order: 2,
        is_visible: true,
        is_locked: false,
        opacity: 1,
        is_wall_layer: false,
        is_fow_layer: false,
        is_background_layer: false,
        is_grid_layer: false,
        is_token_layer: false,
        is_drawing_layer: false,
        is_image_layer: true,
        layer_type: 'image',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        drawings: [],
        images: [],
        layer_data: JSON.stringify({ 
          containers: [],
          layerType: 'IMAGE_LAYER'
        })
      };

      // Layer 4: Fog of War Layer (Group 2) - Top layer (order: 3)
      const fowLayer2 = {
        layer_id: generateLayerId(),
        layer_name: "Fog of War Layer",
        layer_order: 3, // Top
        is_visible: true,
        is_locked: false,
        opacity: 1,
        is_wall_layer: true,
        is_fow_layer: true,
        is_background_layer: false,
        is_grid_layer: false,
        is_token_layer: false,
        is_drawing_layer: false,
        is_image_layer: false,
        layer_type: 'fow',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        drawings: [],
        images: [],
        layer_data: JSON.stringify({ 
          containers: [],
          layerType: 'FOW_LAYER'
        })
      };

      // Set all layers at once to avoid race conditions
      // Sort by layer_order to ensure correct display order
      const allLayers = [mapsLayer1, fowLayer1, mapsLayer2, fowLayer2].sort((a, b) => a.layer_order - b.layer_order);
      
      setLayers(allLayers);
      
      // Set the first Maps layer as active by default
      setActiveLayer(mapsLayer1);
      setActiveLayerId(mapsLayer1.layer_id);
      
      // Create layer groups with a small delay to ensure layers are set in state
      setTimeout(() => {
        // Group 1: Maps Layer (bottom) + FOW Layer
        const groupId1 = generateGroupId();
        const group1 = {
          id: groupId1,
          name: "Group 1",
          layerIds: [mapsLayer1.layer_id, fowLayer1.layer_id],
          color: "#dc3545"
        };
        
        // Group 2: Maps Layer + FOW Layer (top)
        const groupId2 = generateGroupId();
        const group2 = {
          id: groupId2,
          name: "Group 2", 
          layerIds: [mapsLayer2.layer_id, fowLayer2.layer_id],
          color: "#fd7e14"
        };
        
        setLayerGroups([group1, group2]);
      }, 100);
      
      // Successfully created initial 4 layers
      
    } catch (error) {
      console.error('[LayerContext] Error creating initial game layers:', error);
    }
  }, []);

  // Create a new image layer
  const createImageLayer = (layerName) => {
    const newLayer = {
      layer_name: layerName || `Image Layer ${layers.length + 1}`,
      layer_data: JSON.stringify({ 
        containers: [],
        layerType: 'IMAGE_LAYER'
      }),
      is_wall_layer: false,
      is_visible: true,
      layer_type: 'IMAGE_LAYER'
    };
    
    return createLayer(newLayer);
  };
  
  // Create image layer below active layer (for FOW scenarios)
  const createImageLayerBelowActive = useCallback((layerName = 'Image Layer') => {
    try {
      // Create the image layer first
      createLayer({
        layer_name: layerName,
        is_image_layer: true,
        layer_type: 'image',
        layer_order: activeLayer ? activeLayer.layer_order : 0
      });

      // If there's an active FOW layer, create a new FOW layer above the image layer
      if (activeLayer?.is_fow_layer) {
        createLayer({
          layer_name: 'Fog of War',
          is_fow_layer: true,
          layer_type: 'fow',
          layer_order: (activeLayer ? activeLayer.layer_order : 0) + 1
        });
      }

      return true;
    } catch (error) {
      console.error('[LayerContext] Error creating image layer below active:', error);
      throw error;
    }
  }, [activeLayer, createLayer]);
  
  // Convert layer to FOW layer
  const convertToFOWLayer = async (layerId) => {
    const layer = layers.find(l => l.layer_id === layerId);
    if (!layer) return false;
    
    return await updateLayer(layerId, {
      is_wall_layer: true,
      layer_type: 'FOW_LAYER'
    });
  };
  
  // Get active FOW layer (topmost visible FOW layer)
  const getActiveFOWLayer = () => {
    return layers
      .filter(layer => layer.is_wall_layer && layer.is_visible)
      .sort((a, b) => b.layer_order - a.layer_order)[0];
  };
  
  // Check if auto-lock should be active
  const shouldAutoLock = () => {
    const activeLayer = getActiveLayer();
    
    // Auto-lock only if the currently active layer is a FOW layer
    return activeLayer && activeLayer.is_wall_layer;
  };
  
  // Get layers by type
  const getLayersByType = (layerType) => {
    return layers.filter(layer => {
      try {
        const layerData = JSON.parse(layer.layer_data || '{}');
        return layerData.layerType === layerType;
      } catch {
        return false;
      }
    });
  };
  
  // Get image layers
  const getImageLayers = () => {
    return layers.filter(layer => !layer.is_wall_layer);
  };
  
  // Get FOW layers
  const getFOWLayers = () => {
    return layers.filter(layer => layer.is_wall_layer);
  };

  // Create a new layer group
  const createLayerGroup = (layerIds, groupName = null, color = null) => {
    const groupId = generateGroupId();
    
    // Define specific colors for different group types
    let groupColor = color;
    if (!groupColor) {
      if (groupName === "Default Group") {
        groupColor = "#dc3545"; // Red for default group
      } else if (groupName && groupName.includes("Group")) {
        groupColor = "#fd7e14"; // Orange for new groups
      } else {
        groupColor = `hsl(${Math.random() * 360}, 70%, 50%)`; // Random color for others
      }
    }
    
    const newGroup = {
      id: groupId,
      name: groupName || `Group ${layerGroups.length + 1}`,
      layerIds: [...layerIds],
      color: groupColor
    };
    
    setLayerGroups(prev => [...prev, newGroup]);
    return newGroup;
  };

  // Add layer to existing group
  const addLayerToGroup = (layerId, groupId) => {
    setLayerGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, layerIds: [...group.layerIds, layerId] }
        : group
    ));
  };

  // Remove layer from group
  const removeLayerFromGroup = (layerId, groupId) => {
    setLayerGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, layerIds: group.layerIds.filter(id => id !== layerId) }
        : group
    ).filter(group => group.layerIds.length > 0)); // Remove empty groups
  };

  // Remove layer from all groups
  const removeLayerFromAllGroups = (layerId) => {
    setLayerGroups(prev => prev.map(group => ({
      ...group,
      layerIds: group.layerIds.filter(id => id !== layerId)
    })).filter(group => group.layerIds.length > 0)); // Remove empty groups
  };

  // Delete entire group
  const deleteLayerGroup = (groupId) => {
    setLayerGroups(prev => prev.filter(group => group.id !== groupId));
  };

  // Update layer group color
  const updateLayerGroupColor = (groupId, newColor) => {
    setLayerGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, color: newColor }
        : group
    ));
  };

  // Get group that contains a specific layer
  const getLayerGroup = (layerId) => {
    return layerGroups.find(group => group.layerIds.includes(layerId)) || null;
  };

  // Get all layers in the same group as the given layer
  const getGroupedLayers = (layerId) => {
    const group = getLayerGroup(layerId);
    if (!group) return [layerId];
    
    return group.layerIds.filter(id => layers.find(layer => layer.layer_id === id));
  };

  // Toggle visibility for a layer and all layers in its group
  const toggleLayerGroupVisibility = (layerId, newVisibility) => {
    const groupedLayerIds = getGroupedLayers(layerId);
    
    // Update all layers in the group
    setLayers(prevLayers => prevLayers.map(layer => 
      groupedLayerIds.includes(layer.layer_id)
        ? { ...layer, is_visible: newVisibility }
        : layer
    ));
  };

  // Clear all layers (for import functionality)
  const clearAllLayers = useCallback(() => {
    try {
      setLayers([]);
      setActiveLayerByObject(null);
    } catch (error) {
      console.error('[LayerContext] Error clearing layers:', error);
    }
  }, []);

  // Restore multiple layers at once (for game loading functionality)
  const restoreMultipleLayers = useCallback((layersData, activeLayerId = null) => {
    try {
      
      // Sort layers by layer_order to maintain correct order
      const sortedLayersData = [...layersData].sort((a, b) => (a.layer_order || 0) - (b.layer_order || 0));
      
      // Create layers array with proper structure
      const restoredLayers = sortedLayersData.map(layerData => ({
        layer_id: layerData.layer_id || generateLayerId(),
        layer_name: layerData.layer_name || 'Restored Layer',
        layer_order: layerData.layer_order || 0,
        is_visible: layerData.is_visible !== false,
        is_locked: layerData.is_locked || false,
        opacity: layerData.opacity !== undefined ? layerData.opacity : 1,
        is_wall_layer: layerData.is_wall_layer || false,
        is_fow_layer: layerData.is_fow_layer || false,
        is_background_layer: layerData.is_background_layer || false,
        is_grid_layer: layerData.is_grid_layer || false,
        is_token_layer: layerData.is_token_layer || false,
        is_drawing_layer: layerData.is_drawing_layer || false,
        is_image_layer: layerData.is_image_layer || false,
        layer_type: layerData.layer_type || 'general',
        created_at: layerData.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
        drawings: layerData.drawings || [],
        images: layerData.images || [],
        layer_data: layerData.layer_data || JSON.stringify({ lines: [] })
      }));

      // Set all layers at once - no sorting needed as they're already sorted
      setLayers(restoredLayers);
      
      // Set active layer if specified
      if (activeLayerId) {
        const activeLayer = restoredLayers.find(layer => layer.layer_id === activeLayerId);
        if (activeLayer) {
          setActiveLayerByObject(activeLayer);
        }
      } else if (restoredLayers.length > 0) {
        // Default to first layer if no active layer specified
        setActiveLayerByObject(restoredLayers[0]);
      }


      
      return restoredLayers.reduce((mapping, layer, index) => {
        // Return mapping from original ID to restored layer ID
        mapping[sortedLayersData[index].layer_id] = layer.layer_id;
        return mapping;
      }, {});
      
    } catch (error) {
      console.error('[LayerContext] Error restoring multiple layers:', error);
      return {};
    }
  }, []);

  // Restore layer groups (for loading saved games)
  const restoreLayerGroups = useCallback((groupsData) => {
    try {
      if (!Array.isArray(groupsData)) {
        console.warn('[LayerContext] Invalid layer groups data for restoration');
        return;
      }
      
      setLayerGroups(groupsData);
    } catch (error) {
      console.error('[LayerContext] Error restoring layer groups:', error);
    }
  }, []);

  // Load default layers if no layers exist - moved after function definitions to avoid use-before-define
  useEffect(() => {
    // Don't create default layers for new games - they get special 4-layer setup
    if (mapId && mapId !== 'new' && layers.length === 0) {
      // Create default layers using batch creation to avoid race conditions
      createDefaultGameLayers();
    }
    
    // Create initial 4 layers for completely new games
    if (mapId === 'new' && layers.length === 0) {
      createInitialGameLayers();
    }
  }, [mapId, createDefaultGameLayers, createInitialGameLayers, layers.length]);

  const value = {
    layers,
    activeLayerId,
    activeLayer,
    loading,
    error,
    createLayer,
    updateLayer,
    deleteLayer,
    deleteLayers,
    reorderLayers,
    setActiveLayerById,
    setActiveLayerByObject,
    getActiveLayer,
    restoreLayer,
    clearAllLayers,
    restoreMultipleLayers,
    createInitialGameLayers,
    createDefaultGameLayers,
    // New container-related functions
    createImageLayer,
    createImageLayerBelowActive,
    convertToFOWLayer,
    getActiveFOWLayer,
    shouldAutoLock,
    getLayersByType,
    getImageLayers,
    getFOWLayers,
    // Layer grouping functions
    layerGroups,
    createLayerGroup,
    addLayerToGroup,
    removeLayerFromGroup,
    removeLayerFromAllGroups,
    deleteLayerGroup,
    updateLayerGroupColor,
    getLayerGroup,
    getGroupedLayers,
    toggleLayerGroupVisibility,
    restoreLayerGroups
  };

  return (
    <LayerContext.Provider value={value}>
      {children}
    </LayerContext.Provider>
  );
};

export default LayerContext; 